import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, ClipboardList } from "lucide-react";
import { toast } from "sonner";

/**
 * EventChecklistCard
 * ------------------
 * Renders a grouped, mutable planning checklist for a single event. Only
 * the event organizer can see this — parent OrganizerDashboard gates the
 * mount via the `selectedEventId` flow.
 *
 * We fetch on mount and re-fetch on every toggle/add/delete so the
 * completion progress and category groupings stay in sync with the server
 * without local optimism.
 */
export default function EventChecklistCard({ eventId }) {
  const [data, setData] = useState({ items: [], fine_print: "" });
  const [loading, setLoading] = useState(true);
  const [newLabel, setNewLabel] = useState("");
  const [newCategory, setNewCategory] = useState("Custom");
  const [pendingId, setPendingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/events/${eventId}/checklist`);
      setData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load checklist");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (eventId) load(); /* eslint-disable-next-line */ }, [eventId]);

  const toggle = async (item) => {
    setPendingId(item.id);
    try {
      await api.patch(`/events/${eventId}/checklist/${item.id}`, { checked: !item.checked });
      setData((prev) => ({
        ...prev,
        items: prev.items.map((x) => x.id === item.id ? { ...x, checked: !x.checked } : x),
      }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update");
    } finally {
      setPendingId(null);
    }
  };

  const addCustom = async (e) => {
    e.preventDefault();
    const label = newLabel.trim();
    if (label.length < 2) return;
    try {
      const { data: resp } = await api.post(`/events/${eventId}/checklist`, {
        label, category: newCategory,
      });
      setData((prev) => ({ ...prev, items: [...prev.items, resp.item] }));
      setNewLabel("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't add");
    }
  };

  const removeCustom = async (item) => {
    if (!item.custom) return;
    setPendingId(item.id);
    try {
      await api.delete(`/events/${eventId}/checklist/${item.id}`);
      setData((prev) => ({ ...prev, items: prev.items.filter((x) => x.id !== item.id) }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
    } finally {
      setPendingId(null);
    }
  };

  // Group by category preserving the order of first appearance.
  const grouped = {};
  const catOrder = [];
  for (const item of data.items) {
    const cat = item.category || "Other";
    if (!grouped[cat]) { grouped[cat] = []; catOrder.push(cat); }
    grouped[cat].push(item);
  }

  const doneCount = data.items.filter((x) => x.checked).length;
  const totalCount = data.items.length;
  const pct = totalCount > 0 ? Math.round((doneCount / totalCount) * 100) : 0;

  return (
    <div className="card-warm rounded-sm p-6" data-testid="event-checklist-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline inline-flex items-center gap-1.5" style={{ color: "var(--moss)" }}>
            <ClipboardList className="h-3 w-3" /> Planning checklist
          </div>
          <div className="font-serif text-2xl mt-1">Stay on top of the details</div>
        </div>
        <div className="text-right shrink-0" data-testid="checklist-progress">
          <div className="font-serif text-3xl leading-none">{doneCount}<span className="text-lg" style={{ color: "var(--ink-soft)" }}>/{totalCount}</span></div>
          <div className="text-[11px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
            {pct}% complete
          </div>
        </div>
      </div>

      {loading && (
        <div className="mt-4 text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
      )}

      {!loading && (
        <>
          <div className="mt-4 space-y-5">
            {catOrder.map((cat) => (
              <div key={cat} data-testid={`checklist-group-${cat.replace(/\s+/g, '-')}`}>
                <div className="overline mb-2" style={{ color: "var(--terracotta)" }}>{cat}</div>
                <div className="space-y-1">
                  {grouped[cat].map((item) => (
                    <label
                      key={item.id}
                      data-testid={`checklist-item-${item.id}`}
                      className="flex items-start gap-3 px-2 py-1.5 rounded-sm hover:bg-[color:var(--sand)] cursor-pointer"
                      style={{ opacity: pendingId === item.id ? 0.5 : 1 }}
                    >
                      <Checkbox
                        checked={item.checked}
                        onCheckedChange={() => toggle(item)}
                        data-testid={`checklist-checkbox-${item.id}`}
                        className="mt-0.5"
                      />
                      <span
                        className={`flex-1 text-sm ${item.checked ? "line-through" : ""}`}
                        style={{ color: item.checked ? "var(--ink-soft)" : "var(--ink)" }}
                      >
                        {item.label}
                      </span>
                      {item.custom && (
                        <button
                          type="button"
                          data-testid={`checklist-delete-${item.id}`}
                          onClick={(e) => { e.preventDefault(); removeCustom(item); }}
                          className="text-[11px] opacity-70 hover:opacity-100"
                          style={{ color: "var(--terracotta)" }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={addCustom} className="mt-5 flex gap-2 flex-wrap" data-testid="checklist-add-form">
            <Input
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Add a custom item (e.g. Rent generator)"
              maxLength={140}
              data-testid="checklist-add-input"
              className="flex-1 min-w-[240px]"
            />
            <Input
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              maxLength={40}
              data-testid="checklist-add-category"
              className="max-w-[180px]"
            />
            <Button type="submit" className="btn-outline rounded-full"
              data-testid="checklist-add-submit"
              disabled={newLabel.trim().length < 2}>
              <Plus className="h-4 w-4 mr-1.5" /> Add
            </Button>
          </form>

          {data.fine_print && (
            <p className="mt-4 text-[11px] leading-relaxed" style={{ color: "var(--ink-soft)" }}
               data-testid="checklist-fine-print">
              {data.fine_print}
            </p>
          )}
        </>
      )}
    </div>
  );
}
